import { Add } from '@mui/icons-material';
import { CircularProgress, Stack, type Theme, Typography, useMediaQuery } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import DashboardCard from '/@/components/bayer/dashboard-card';
import Header from '/@/components/bayer/header';
import MobileHeader from '/@/components/bayer/header/mobile-header';
import PageContainer from '/@/components/bayer/page-container';
import MoreOptions from '/@/components/more-options';
import { type ActivityModality, type ActivityType, useActivities } from '/@/hooks/activities';
import { useUserNodes } from '/@/hooks/nodes';
import { useIsValidatedSquad } from '/@/hooks/session';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import ActivityCard from './components/activity-card';
import ActivityFormModal from './components/activity-form-modal';
import SquadFilterPills from './components/squad-filter-pills';
import { ACTIVITY_TYPE_OPTIONS, MODALITY_OPTIONS } from './constants';

type PeriodOption = '' | '7d' | '30d' | 'month' | 'lastMonth';

const getPeriodRange = (period: PeriodOption): { dateFrom?: string; dateTo?: string } => {
    const today = dayjs();
    switch (period) {
        case '7d':
            return { dateFrom: today.subtract(7, 'day').format('YYYY-MM-DD'), dateTo: today.format('YYYY-MM-DD') };
        case '30d':
            return { dateFrom: today.subtract(30, 'day').format('YYYY-MM-DD'), dateTo: today.format('YYYY-MM-DD') };
        case 'month':
            return {
                dateFrom: today.startOf('month').format('YYYY-MM-DD'),
                dateTo: today.endOf('month').format('YYYY-MM-DD'),
            };
        case 'lastMonth': {
            const lastMonth = today.subtract(1, 'month');
            return {
                dateFrom: lastMonth.startOf('month').format('YYYY-MM-DD'),
                dateTo: lastMonth.endOf('month').format('YYYY-MM-DD'),
            };
        }
        default:
            return {};
    }
};

const ActivitiesList = () => {
    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const isValidatedSquad = useIsValidatedSquad();

    const [period, setPeriod] = useState<PeriodOption>('');
    const [activityTypeFilter, setActivityTypeFilter] = useState<ActivityType | ''>('');
    const [modalityFilter, setModalityFilter] = useState<ActivityModality | ''>('');
    const [selectedSquadId, setSelectedSquadId] = useState<number | null>(null);

    const [formModalOpen, setFormModalOpen] = useState(false);
    const [editingActivityId, setEditingActivityId] = useState<number | undefined>(undefined);

    const { dateFrom, dateTo } = getPeriodRange(period);

    const squadsQuery = useUserNodes(
        { type: 'squad', includeChildren: true, page_size: 1000 },
        { enabled: isValidatedSquad }
    );
    const squads = useMemo(() => squadsQuery.data?.results || [], [squadsQuery.data]);

    const selectSquad = (squadId: number) => {
        setSelectedSquadId((prev) => (prev === squadId ? null : squadId));
    };

    const activitiesQuery = useActivities({
        page: 1,
        pageSize: 1000,
        activityType: activityTypeFilter || undefined,
        modality: modalityFilter || undefined,
        dateFrom,
        dateTo,
        ordering: '-date',
        squadNodeIds: selectedSquadId !== null ? [selectedSquadId] : undefined,
    });

    const activities = useMemo(() => activitiesQuery.data?.results || [], [activitiesQuery.data]);

    const kpis = useMemo(() => {
        const totalActivities = activitiesQuery.data?.count ?? activities.length;
        const totalParticipants = activities.reduce((sum, activity) => sum + activity.participantsCount, 0);
        const activitiesWithParticipants = activities.filter((activity) => activity.participantsCount > 0);
        const averageAttendance = activitiesWithParticipants.length
            ? Math.round(
                  (activitiesWithParticipants.reduce(
                      (sum, activity) => sum + (activity.attendanceSummary?.si ?? 0) / activity.participantsCount,
                      0
                  ) /
                      activitiesWithParticipants.length) *
                      100
              )
            : 0;
        const totalBudget = activities.reduce((sum, activity) => sum + Number(activity.budget || 0), 0);

        return { totalActivities, totalParticipants, averageAttendance, totalBudget };
    }, [activities, activitiesQuery.data?.count]);

    const handleOpenCreate = () => {
        setEditingActivityId(undefined);
        setFormModalOpen(true);
    };

    const handleOpenEdit = (activityId: number) => {
        setEditingActivityId(activityId);
        setFormModalOpen(true);
    };

    return (
        <PageContainer
            header={
                isSmOrLower ? (
                    <MobileHeader
                        title={_('Actividades')}
                        moreButton={
                            isValidatedSquad ? undefined : (
                                <MoreOptions
                                    menuItems={[
                                        {
                                            label: _('Nueva actividad'),
                                            onClick: () => {
                                                handleOpenCreate();
                                            },
                                        },
                                    ]}
                                />
                            )
                        }
                    />
                ) : undefined
            }
            heroHeader={
                isSmOrLower ? undefined : (
                    <Header
                        title={_('Actividades')}
                        subtitle={_('Registro de entrenamientos y encuentros de generación de demanda del equipo.')}
                        buttons={
                            isValidatedSquad
                                ? []
                                : [
                                      {
                                          children: _('Nueva actividad'),
                                          variant: 'contained',
                                          color: 'secondary',
                                          startIcon: <Add />,
                                          onClick: handleOpenCreate,
                                      },
                                  ]
                        }
                        variant='full-gray'
                    />
                )
            }
        >
            {isValidatedSquad && squads.length > 0 && (
                <SquadFilterPills squads={squads} selectedSquadId={selectedSquadId} onSelectSquad={selectSquad} />
            )}

            <Grid2 container spacing={3} mb={3}>
                <Grid2 xs={12} sm={6} md={3}>
                    <DashboardCard
                        title={_('Actividades totales')}
                        isLoading={activitiesQuery.isLoading}
                        number={activitiesQuery.data?.count}
                    />
                </Grid2>
                <Grid2 xs={12} sm={6} md={3}>
                    <DashboardCard
                        title={_('Participantes')}
                        isLoading={activitiesQuery.isLoading}
                        number={String(kpis.totalParticipants).padStart(1, '0')}
                    />
                </Grid2>
                <Grid2 xs={12} sm={6} md={3}>
                    <DashboardCard
                        title={_('Asistencia promedio')}
                        isLoading={activitiesQuery.isLoading}
                        number={`${kpis.averageAttendance}%`}
                    />
                </Grid2>
                <Grid2 xs={12} sm={6} md={3}>
                    <DashboardCard
                        title={_('Presupuesto total gastado')}
                        isLoading={activitiesQuery.isLoading}
                        number={`$${kpis.totalBudget}`}
                    />
                </Grid2>
            </Grid2>

            <Grid2 container spacing={2} mb={3}>
                <Grid2 xs={12} sm={4}>
                    <MuiSelect
                        inputLabel={_('Período')}
                        value={period}
                        onChange={(e) => setPeriod(e.target.value as PeriodOption)}
                        options={[
                            { value: '', label: _('Todos') },
                            { value: '7d', label: _('Últimos 7 días') },
                            { value: '30d', label: _('Últimos 30 días') },
                            { value: 'month', label: _('Este mes') },
                            { value: 'lastMonth', label: _('Mes pasado') },
                        ]}
                        formControlProps={{ fullWidth: true }}
                    />
                </Grid2>
                <Grid2 xs={12} sm={4}>
                    <MuiSelect
                        inputLabel={_('Actividad')}
                        value={activityTypeFilter}
                        onChange={(e) => setActivityTypeFilter(e.target.value as ActivityType | '')}
                        options={[{ value: '', label: _('Todos') }, ...ACTIVITY_TYPE_OPTIONS]}
                        formControlProps={{ fullWidth: true }}
                    />
                </Grid2>
                <Grid2 xs={12} sm={4}>
                    <MuiSelect
                        inputLabel={_('Modalidad')}
                        value={modalityFilter}
                        onChange={(e) => setModalityFilter(e.target.value as ActivityModality | '')}
                        options={[{ value: '', label: _('Todos') }, ...MODALITY_OPTIONS]}
                        formControlProps={{ fullWidth: true }}
                    />
                </Grid2>
            </Grid2>

            {activitiesQuery.isLoading ? (
                <Stack alignItems='center' py={4}>
                    <CircularProgress />
                </Stack>
            ) : activities.length === 0 ? (
                <Stack alignItems='center' py={4} spacing={1}>
                    <Typography variant='subtitle1' color='primary'>
                        {_('No hay actividades registradas')}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        {_('Registrá entrenamientos y encuentros de generación de demanda con productores.')}
                    </Typography>
                </Stack>
            ) : (
                <Stack spacing={2}>
                    {activities.map((activity) => (
                        <ActivityCard key={activity.id} activity={activity} onEdit={handleOpenEdit} />
                    ))}
                </Stack>
            )}

            <ActivityFormModal
                open={formModalOpen}
                onClose={() => setFormModalOpen(false)}
                activityId={editingActivityId}
            />
        </PageContainer>
    );
};

export default ActivitiesList;
